import type { ReactNode } from 'react';

import { type Role, Tab, type TrialType } from '@revelio/data-access';
import { Switch } from '@revelio/ui';

import styles from './impersonationBanner.module.css';
import {
  formatExpirationDate,
  formatValueLabel,
} from './impersonationBanner.utils';

interface ImpersonationUserTooltipContentProps {
  resolvedTrialUser: boolean | undefined;
  resolvedTrialType: TrialType | undefined;
  resolvedExpirationDate: string | undefined;
  resolvedRole: Role | undefined;
}

export function ImpersonationUserTooltipContent({
  resolvedTrialUser,
  resolvedTrialType,
  resolvedExpirationDate,
  resolvedRole,
}: ImpersonationUserTooltipContentProps) {
  const rows: ReactNode[] = [];

  if (resolvedTrialUser !== undefined) {
    rows.push(
      <div key="trial-user" className={styles.tooltipRow}>
        <span className={styles.tooltipLabel}>Trial User</span>
        <Switch
          size="sm"
          inactiveLabel="Off"
          label="On"
          checked={resolvedTrialUser ?? false}
          disabled={true}
          readOnly={true}
          className={styles.readOnlySwitch}
        />
      </div>
    );
  }

  if (resolvedTrialType) {
    rows.push(
      <div key="trial-type" className={styles.tooltipRow}>
        <span className={styles.tooltipLabel}>Trial Type</span>
        <span className={styles.tooltipValue}>
          {formatValueLabel(String(resolvedTrialType))}
        </span>
      </div>
    );
  }

  if (resolvedExpirationDate) {
    rows.push(
      <div key="trial-ends" className={styles.tooltipRow}>
        <span className={styles.tooltipLabel}>Trial Ends</span>
        <span className={styles.tooltipValue}>
          {formatExpirationDate(resolvedExpirationDate)}
        </span>
      </div>
    );
  }

  if (resolvedRole) {
    rows.push(
      <div key="role" className={styles.tooltipRow}>
        <span className={styles.tooltipLabel}>Role</span>
        <span className={styles.tooltipValue}>
          {formatValueLabel(String(resolvedRole))}
        </span>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <span className={styles.tooltipValue}>User settings unavailable</span>
    );
  }

  return <div className={styles.tooltipRows}>{rows}</div>;
}

interface ImpersonationClientTooltipContentProps {
  resolvedClientName: string | undefined;
  resolvedTabs: Tab[] | undefined;
  resolvedLinkupPostings: boolean | undefined;
  resolvedReportTypes: string[];
  resolvedDatasetLabels: string[];
}

export function ImpersonationClientTooltipContent({
  resolvedClientName,
  resolvedTabs,
  resolvedLinkupPostings,
  resolvedReportTypes,
  resolvedDatasetLabels,
}: ImpersonationClientTooltipContentProps) {
  const rows: ReactNode[] = [];
  const sections: ReactNode[] = [];
  const hasReportsTab = resolvedTabs?.includes(Tab.Reports) ?? false;
  const hasDeliverablesTab = resolvedTabs?.includes(Tab.Deliverables) ?? false;

  if (resolvedClientName) {
    rows.push(
      <div key="client" className={styles.tooltipRow}>
        <span className={styles.tooltipLabel}>Client Group</span>
        <span className={styles.tooltipValue}>{resolvedClientName}</span>
      </div>
    );
  }

  if (resolvedLinkupPostings !== undefined) {
    rows.push(
      <div key="linkup-postings" className={styles.tooltipRow}>
        <span className={styles.tooltipLabel}>Website Postings</span>
        <Switch
          size="sm"
          inactiveLabel="Off"
          label="On"
          checked={Boolean(resolvedLinkupPostings)}
          disabled={true}
          readOnly={true}
          className={styles.readOnlySwitch}
        />
      </div>
    );
  }

  if (resolvedTabs && resolvedTabs.length > 0) {
    rows.push(
      <div key="tabs" className={styles.tooltipRowStart}>
        <span className={styles.tooltipLabel}>Tabs</span>
        <div className={styles.tooltipChips}>
          {resolvedTabs.map((tab, index) => (
            <span key={`${tab}-${index}`} className={styles.valueChip}>
              {formatValueLabel(String(tab))}
            </span>
          ))}
        </div>
      </div>
    );
  }

  if (rows.length > 0) {
    sections.push(
      <div key="client-group" className={styles.tooltipRows}>
        {rows}
      </div>
    );
  }

  if (hasReportsTab && resolvedReportTypes.length > 0) {
    sections.push(
      <div key="report-types" className={styles.tooltipRowStart}>
        <span className={styles.tooltipLabel}>Report Types</span>
        <div className={styles.tooltipChips}>
          {resolvedReportTypes.map((reportType, index) => (
            <span key={`${reportType}-${index}`} className={styles.valueChip}>
              {reportType}
            </span>
          ))}
        </div>
      </div>
    );
  }

  if (hasDeliverablesTab && resolvedDatasetLabels.length > 0) {
    sections.push(
      <div key="datasets" className={styles.tooltipRowStart}>
        <span className={styles.tooltipLabel}>Subscribed Datasets</span>
        <div className={styles.tooltipChips}>
          {resolvedDatasetLabels.map((dataset, index) => (
            <span key={`${dataset}-${index}`} className={styles.valueChip}>
              {dataset}
            </span>
          ))}
        </div>
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <span className={styles.tooltipValue}>
        Client group settings unavailable
      </span>
    );
  }

  return <div className={styles.tooltipSections}>{sections}</div>;
}
